/** @jsxImportSource @emotion/react */
import React from 'react'
import css from '@emotion/css/macro'
import { Link } from 'react-router-dom'
import colors from '@sportninja/common/constants/appColors'
import { getImageThumbnailId } from '@sportninja/common/utils/utils'
import { ROUTES } from '@sportninja/common/constants/app'
import { StatColumn, getStatValue } from '../../utils/statColumns'
import Text from 'src/components/ts-components/Text'

interface DetailedStatsTableProps {
  title: string
  data: any[]
  columns: StatColumn[]
}

const TEAM_CELL_WIDTH = 200

const DetailedStatsTable = ({
  title,
  data,
  columns,
}: DetailedStatsTableProps) => {
  const sortedData = [...data].sort((a, b) => {
    const aDate = a.season?.starts_at || ''
    const bDate = b.season?.starts_at || ''
    return bDate.localeCompare(aDate)
  })

  return (
    <div
      css={css`
        background-color: ${colors.SECONDARY_400};
        border-radius: 8px;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 16px;
      `}
    >
      <Text variant='heading5' weight='semiBold' color={colors.NEUTRAL_0}>
        {title}
      </Text>
      <div
        css={css`
          border: 1px solid ${colors.SECONDARY_300};
          border-radius: 8px;
          overflow: hidden;
          overflow-x: auto;
        `}
      >
        {/* Header row */}
        <div
          css={css`
            display: flex;
            padding: 4px 16px;
            min-height: 26px;
            align-items: center;
            background-color: ${colors.SECONDARY_300};
            min-width: fit-content;
          `}
        >
          <span
            css={css`
              width: ${TEAM_CELL_WIDTH}px;
              min-width: ${TEAM_CELL_WIDTH}px;
              font-family: 'Inter', sans-serif;
              font-weight: 500;
              font-size: 12px;
              color: ${colors.NEUTRAL_70};
            `}
          >
            Team
          </span>
          {columns.map((col) => (
            <span
              key={col.abbr}
              title={col.tooltip}
              css={css`
                flex: 1;
                text-align: center;
                min-width: 50px;
                font-family: 'Inter', sans-serif;
                font-weight: 500;
                font-size: 12px;
                color: ${colors.NEUTRAL_70};
              `}
            >
              {col.title}
            </span>
          ))}
        </div>

        {/* Data rows */}
        {sortedData.map((record, idx) => (
          <div
            key={`${record.team?.id}-${record.season?.id}-${idx}`}
            css={css`
              display: flex;
              align-items: center;
              padding: 8px 16px;
              min-height: 38px;
              background-color: ${idx % 2 === 0
                ? colors.SECONDARY_500
                : colors.SECONDARY_400};
              min-width: fit-content;

              &:hover {
                background-color: ${colors.SECONDARY_200};
              }
            `}
          >
            {/* Team cell */}
            <div
              css={css`
                width: ${TEAM_CELL_WIDTH}px;
                min-width: ${TEAM_CELL_WIDTH}px;
                display: flex;
                align-items: center;
                gap: 8px;
              `}
            >
              <Link
                to={`${ROUTES.TEAM_ROOT}/${record.team?.id}`}
                css={css`
                  text-decoration: none;
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  min-width: 0;
                `}
              >
                <img
                  src={
                    getImageThumbnailId(record.team) ||
                    '/images/generic_placeholders/placeholder_team.png'
                  }
                  alt={record.team?.name || ''}
                  css={css`
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    object-fit: contain;
                    border: 1px solid ${colors.NEUTRAL_50};
                    background-color: ${colors.NEUTRAL_0};
                    flex-shrink: 0;
                  `}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    const target = e.target as HTMLImageElement
                    target.src =
                      '/images/generic_placeholders/placeholder_team.png'
                  }}
                />
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    min-width: 0;
                  `}
                >
                  <span
                    css={css`
                      font-family: 'Inter', sans-serif;
                      font-weight: 500;
                      font-size: 13px;
                      color: ${colors.NEUTRAL_0};
                      white-space: nowrap;
                      overflow: hidden;
                      text-overflow: ellipsis;
                    `}
                  >
                    {record.team?.name}
                  </span>
                  {record.season?.name && (
                    <Link
                      to={`${ROUTES.SCHEDULE_ROOT}/${record.season?.id}`}
                      css={css`
                        text-decoration: none;
                      `}
                    >
                      <span
                        css={css`
                          font-family: 'Inter', sans-serif;
                          font-weight: 400;
                          font-size: 11px;
                          color: ${colors.ATTENDANCE_GRAY};
                          white-space: nowrap;
                          overflow: hidden;
                          text-overflow: ellipsis;

                          &:hover {
                            text-decoration: underline;
                          }
                        `}
                      >
                        {record.season.name}
                      </span>
                    </Link>
                  )}
                </div>
              </Link>
            </div>

            {/* Stat cells */}
            {columns.map((col) => (
              <span
                key={col.abbr}
                css={css`
                  flex: 1;
                  text-align: center;
                  min-width: 50px;
                  font-family: 'Inter', sans-serif;
                  font-weight: 400;
                  font-size: 14px;
                  color: ${colors.NEUTRAL_0};
                `}
              >
                {getStatValue(record, col.abbr, col.defaultValue)}
              </span>
            ))}
          </div>
        ))}

        {sortedData.length === 0 && (
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              padding: 24px 16px;
              font-family: 'Inter', sans-serif;
              font-size: 14px;
              color: ${colors.NEUTRAL_50};
            `}
          >
            No statistics available
          </div>
        )}
      </div>
    </div>
  )
}

export default DetailedStatsTable
